import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Container } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import { format } from 'date-fns'

import { AppBarPatient } from 'ui/partials/AppBar'
import type {
  MedicalInstitutionProps,
  PasscodeEntryApiProps,
} from 'types/applicant'
import { StackItem } from 'ui/layout/StackItem'
import { StackItemTextField } from 'ui/shorthands/StackItemTextField'
import { Text1 } from 'ui/shorthands/Texts'
import { Layout } from 'ui/layout/Layout'
import QuestionTooltip from 'ui/shorthands/QuestionTooltip'
import { parseDatetimeSecondString } from 'ui/utils/dateFormatUtils'
import { isPastJstNow } from 'ui/utils/jstDatetime'
import validationSchema from 'ui/shorthands/validationSchema'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
  },

  category: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    '&:last-child': {
      marginBottom: theme.spacing(4),
    },
  },

  submitWrapper: {
    paddingTop: theme.spacing(4),
    paddingRight: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

export type PasscodeEntryProps = {
  med: MedicalInstitutionProps
  entry: PasscodeEntryApiProps
  onSubmit: (data: { [name: string]: any }) => Promise<void>
}

const PasscodeEntry: React.FC<PasscodeEntryProps> = ({
  med,
  entry,
  onSubmit,
}: PasscodeEntryProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const schema = yup.object().shape({
    pass_code: validationSchema.passCode(),
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FieldValues>({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  })

  const expiredJstDate = parseDatetimeSecondString(entry.expiration)
  const formattedJstDate = format(
    expiredJstDate,
    t('datetime.yyyy年 M月d日 HH:mm')
  )
  const isExpired = isPastJstNow(expiredJstDate)
  const isCanceled = entry.is_canceled

  const submitHandler = (data: FieldValues) =>
    onSubmit(data as { pass_code: string }).catch(() => {
      // NOTE: このエラーしか通さない
      setError('pass_code', {
        type: 'invalid pass code',
        message: t('errors.パスコードが間違っています'),
      })
    })

  return (
    <Layout
      header={
        <AppBarPatient>
          {med.name} {t('予約')}
        </AppBarPatient>
      }
      showFooter
    >
      <Container maxWidth="md">
        <form onSubmit={handleSubmit(submitHandler)}>
          <div className={classes.category}>
            <StackItem label={t('予約ID')}>
              <Text1>{entry.reservation_number}</Text1>
            </StackItem>
            {isExpired || isCanceled ? (
              <StackItem label={t('ステータス')}>
                <Text1>
                  {isCanceled ? t('予約取消') : isExpired && t('有効期限切れ')}
                </Text1>
              </StackItem>
            ) : (
              <StackItemTextField
                description={t(
                  '有効期限: {{formattedJstDate}}（日本時間） まで',
                  { formattedJstDate }
                )}
                name="pass_code"
                label={
                  <>
                    {t('パスコード')}
                    <QuestionTooltip>
                      {t('メールに記載されています。')}
                    </QuestionTooltip>
                  </>
                }
                errorMessage={errors?.pass_code?.message}
                control={control}
                onChange={() => {
                  clearErrors('pass_code')
                }}
                alignTop
              />
            )}
          </div>
          {!isExpired && !isCanceled && (
            <div className={classes.submitWrapper}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!!Object.keys(errors).length}
              >
                {t('送信')}
              </Button>
            </div>
          )}
        </form>
      </Container>
    </Layout>
  )
}

export default PasscodeEntry
